import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TimeAgo from 'react-timeago';
import {
  CopyToClipboard,
  Box,
  FormGroup,
  Gap,
  IconButton,
  Input,
  RadioButton,
  SaveBar,
} from '@salecycle/ui-component-library';
import { Text, BlueText, Sidebar, SidebarSection, SidebarSectionSubtitle, SidebarSectionTitle } from '../styles';
import { useTemplatesListDispatch, useTemplatesListState } from '../../context/UseTemplatesListContext';
import { useUserContext } from 'core/users/UserContext';
import { SupportedLanguageButtonContainer, SupportedLanguageButtonItem } from './SelectedTemplateSidebar';
import { generatePath, useParams } from 'react-router';
import { DetermineLinkButton } from './DetermineLinkButton';
import { getEditRoute } from './utils/getEditRoute';
import ConfirmationModal from '@salecycle/experimental/ConfirmationModal';
import { useAddLanguage } from './hooks/useAddLanguage';
import { useMutation, useQueryClient } from 'react-query';
import { updateTemplateContainer } from '../../utils/queries';
import useNotifications from 'core/notifications/useNotifications';
import { DeleteTemplateButton } from 'sections/design/template-management/sections/template-archive/components/DeleteTemplateButton';

export interface CommonSelectedTemplateSidebarProps {
  showModal: boolean;
  setShowModal(show: boolean): void;
  showEmbeddedOptions?: boolean;
  showEdit?: boolean;
  /** Additional content which will be added after the "Add languages" section. */
  additionalContent?: ReactNode;
}

export const CommonSelectedTemplateSidebar = ({
  showEmbeddedOptions,
  showEdit,
  showModal,
  setShowModal,
  additionalContent,
}: CommonSelectedTemplateSidebarProps) => {
  const { t } = useTranslation(['common', 'designs', 'supportedLanguages', 'templates']);
  const dispatch = useTemplatesListDispatch();
  const { siteId } = useParams<{ siteId: string }>();
  const { showNotification } = useNotifications();
  const queryClient = useQueryClient();
  const { selectedTemplate, supportedLanguages } = useTemplatesListState();

  const { getUser } = useUserContext();
  const [createdBy, setCreatedBy] = useState(getUser(selectedTemplate.audit.createdBy)?.name ?? '');

  const [lastUpdatedBy, setLastUpdatedBy] = useState(getUser(selectedTemplate.audit.lastUpdatedBy)?.name ?? '');
  const [implementedLanguages, setImplementedLanguages] = useState(selectedTemplate.supportedLanguages ?? []);
  const [unimplementedLanguages, setUnimplementedLanguages] = useState([]);
  const [templateName, setTemplateName] = useState(selectedTemplate.templateName);
  const [templateLanguageOptions, setTemplateLanguageOptions] = useState({ language: '', friendlyLanguageName: '' });
  const [isEmbedded, setIsEmbedded] = useState(selectedTemplate?.isEmbedded ?? false);

  const updateContainerMutation = useMutation(updateTemplateContainer);

  // Initial templates list
  useEffect(() => {
    setImplementedLanguages(selectedTemplate.supportedLanguages);

    if (!selectedTemplate.supportedLanguages) {
      setUnimplementedLanguages(supportedLanguages);
    } else {
      setUnimplementedLanguages(
        supportedLanguages
          .filter(sl => selectedTemplate.supportedLanguages.findIndex(tsl => tsl.language === sl) === -1)
          ?.sort(),
      );
    }
  }, [selectedTemplate.id, selectedTemplate.supportedLanguages, supportedLanguages]);

  useEffect(() => {
    setTemplateName(selectedTemplate.templateName);
    setLastUpdatedBy(getUser(selectedTemplate.audit.lastUpdatedBy)?.name ?? '');
    setCreatedBy(getUser(selectedTemplate.audit.createdBy)?.name ?? '');
    setIsEmbedded(selectedTemplate?.isEmbedded ?? false);
  }, [selectedTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeSideBar = useCallback(() => {
    dispatch({
      type: 'RESET_SELECTED_TEMPLATE',
    });
  }, [dispatch]);

  const onChangeTemplateName = useCallback((value: string) => {
    setTemplateName(value);
  }, []);

  const toggleEmbedded = useCallback(() => {
    setIsEmbedded(!isEmbedded);
  }, [isEmbedded]);

  const handleEditClick = useCallback(
    (language: string) => {
      window.open(
        generatePath(getEditRoute(selectedTemplate), { siteId, id: selectedTemplate.id, language }) + '?newtab=1',
        '_blank',
      );
    },
    [selectedTemplate, siteId],
  );

  const handleLanguageClick = useCallback(
    (language: string, friendlyLanguageName: string) => {
      setTemplateLanguageOptions({ language, friendlyLanguageName });
      setShowModal(true);
    },
    [setShowModal],
  );

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, [setShowModal]);

  const addLanguage = useAddLanguage(selectedTemplate);

  const handleOkButtonClick = useCallback(() => {
    closeModal();
    addLanguage(templateLanguageOptions.language);
  }, [addLanguage, closeModal, templateLanguageOptions.language]);

  const handleSaveClick = useCallback(() => {
    const {
      id: templateId,
      enrichmentConfigIds,
      type,
      channel,
      supportedLanguages: templateLanguages,
    } = selectedTemplate;

    updateContainerMutation.mutate(
      {
        siteId,
        templateId,
        enrichmentConfigIds,
        templateName,
        type,
        channel,
        templateLanguages,
        isEmbedded,
        t,
      },
      {
        onSuccess: () => {
          showNotification(t('designs:designGallery.form.comment.notifications.saveTemplateSuccess'), 'normal');
          queryClient.invalidateQueries(['templates']);
        },
        onError: () => {
          showNotification(t('designs:designGallery.form.comment.notifications.cloneTemplateError'), 'error');
        },
      },
    );
  }, [isEmbedded, queryClient, selectedTemplate, showNotification, siteId, t, templateName, updateContainerMutation]);

  const onDiscardButtonClicked = useCallback(() => {
    setTemplateName(selectedTemplate.templateName);
  }, [selectedTemplate.templateName]);

  return (
    <Sidebar>
      <SidebarSection>
        <Box verticalPositioning="center">
          <SidebarSectionTitle>{t('designs:designGallery.sidebar.summary')}</SidebarSectionTitle>
          <IconButton onClick={closeSideBar} icon="times" ariaLabel={t('designs:designGallery.sidebar.close')} />
        </Box>
        <SidebarSectionSubtitle data-testid="TemplateNameSubtitle">
          <Input
            label={t('designs:designGallery.sidebar.templateName')}
            ariaLabel={t('designs:designGallery.sidebar.templateName')}
            value={templateName}
            onChange={onChangeTemplateName}
          />
        </SidebarSectionSubtitle>
        <SidebarSection>
          <CopyToClipboard
            label={t('designs:designGallery.sidebar.templateKey')}
            value={selectedTemplate.id}
            hint={t('designs:designGallery.sidebar.templateKeyHint')}
          />
          <Gap bottom={30} />
          {showEmbeddedOptions ? (
            <FormGroup label={t('designs:designGallery.form.displayType')} variation="inline">
              <RadioButton
                selected={!isEmbedded}
                onClick={toggleEmbedded}
                label={t('designs:designGallery.form.popUpLabel')}
                aria-label={t('designs:designGallery.form.popUpLabelAria')}
              />
              <RadioButton
                selected={isEmbedded}
                onClick={toggleEmbedded}
                label={t('designs:designGallery.form.embeddedLabel')}
                aria-label={t('designs:designGallery.form.embeddedLabelAria')}
              />
            </FormGroup>
          ) : null}
          <Gap bottom={20} />
          <SidebarSection data-testid="SaveButton">
            <SaveBar
              paddingRight="0"
              onSaveButtonClicked={handleSaveClick}
              onDiscardButtonClicked={onDiscardButtonClicked}
              enableSaveButton={
                templateName !== selectedTemplate.templateName ||
                (showEmbeddedOptions ? isEmbedded !== (selectedTemplate.isEmbedded ?? false) : false)
              }
            />
          </SidebarSection>
          <Gap bottom={20} />
          <div>
            <Text>{t('designs:designGallery.sidebar.createdBy')}</Text>
            <BlueText data-testid="CreatedBy">{createdBy}</BlueText>
          </div>
          <Gap bottom={10} />
          <div>
            <Text>{t('designs:designGallery.sidebar.lastEdited')}</Text>
            <BlueText data-testid="LastUpdatedBy">{lastUpdatedBy}</BlueText> -{' '}
            <TimeAgo live={false} date={Number(selectedTemplate.audit.lastUpdatedAt)} />
          </div>
        </SidebarSection>
      </SidebarSection>
      <Gap bottom={20} />
      {showEdit && (
        <>
          <Gap bottom={20} />
          <SidebarSection data-testid="EditLanguagesSection">
            <SidebarSectionSubtitle data-testid="TemplateLanguageEditSubTitle">
              {t('common.edit')}
            </SidebarSectionSubtitle>
            <SupportedLanguageButtonContainer>
              {implementedLanguages.map(sl => {
                return (
                  <SupportedLanguageButtonItem key={sl.language}>
                    <DetermineLinkButton
                      language={sl.language}
                      handleEditClick={handleEditClick}
                      handleLanguageClicked={handleLanguageClick}
                    />
                  </SupportedLanguageButtonItem>
                );
              })}
            </SupportedLanguageButtonContainer>
          </SidebarSection>
        </>
      )}
      <Gap bottom={20} />
      <SidebarSection data-testid="AddLanguagesSection">
        <SidebarSectionSubtitle data-testid="TemplateLanguageCreateSubTitle">
          {t('common.create')}
        </SidebarSectionSubtitle>
        <SupportedLanguageButtonContainer>
          {unimplementedLanguages.map(sl => {
            return (
              <SupportedLanguageButtonItem key={sl}>
                <DetermineLinkButton
                  language={sl}
                  handleEditClick={handleEditClick}
                  handleLanguageClicked={handleLanguageClick}
                />
              </SupportedLanguageButtonItem>
            );
          })}
        </SupportedLanguageButtonContainer>
      </SidebarSection>
      {additionalContent}
      <SidebarSection data-testid="DeleteTemplateSection">
        <DeleteTemplateButton selectedTemplate={selectedTemplate} />
      </SidebarSection>
      <ConfirmationModal
        cancelButtonLabel={t('common.cancel')}
        okButtonLabel={t('common.create')}
        heading={t('templates:modal.heading', { language: templateLanguageOptions.friendlyLanguageName })}
        description={t('templates:modal.description')}
        isOpen={showModal}
        onCancelButtonClicked={closeModal}
        onCloseButtonClicked={closeModal}
        onOkButtonClicked={handleOkButtonClick}
      />
    </Sidebar>
  );
};

CommonSelectedTemplateSidebar.defaultProps = {
  showEmbeddedOptions: false,
  showEdit: true,
};
